import React from "react";
import { Box, Container, Typography, useTheme, alpha } from "@mui/material";
import { MotionBox } from "@rusha/shared-theme";
import { JsonLd } from "../common/JsonLd";

interface MeteredUnit {
  label: string;
  price: string;
}

interface Tier {
  name: string;
  price: string;
  priceValue: string;
  unit: string;
  description: string;
  highlighted: boolean;
  cta: string;
  ctaHref: string;
  meteredUnits?: MeteredUnit[];
}

const TIERS: Tier[] = [
  {
    name: "Free",
    price: "$0",
    priceValue: "0",
    unit: "/mo",
    description: "Up to 3 systems, 720 compute hours/month",
    highlighted: false,
    cta: "Start free",
    ctaHref: "https://app.rusha.tierravivaai.net",
  },
  {
    name: "Paid",
    price: "Usage-based",
    priceValue: "0",
    unit: "",
    description: "Pay only for what you use. No per-user fees.",
    highlighted: true,
    cta: "Start free trial",
    ctaHref: "https://app.rusha.tierravivaai.net",
    meteredUnits: [
      { label: "CPU", price: "$0.0000048/millicore-hr" },
      { label: "Build", price: "$0.008/min" },
      { label: "Deploy", price: "$0.015/min" },
      { label: "Messages", price: "$0.02/1k" },
      { label: "Monitoring", price: "$0.05/hr" },
    ],
  },
];

const Pricing: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      id="pricing"
      aria-labelledby="price-title"
      sx={{
        py: { xs: 8, md: 10 },
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="lg">
        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}
        >
          <Typography
            component="h2"
            id="price-title"
            variant="h2"
            sx={{
              mb: 1,
              fontWeight: 800,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
            }}
          >
            Simple, transparent pricing
          </Typography>
        </MotionBox>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
            gap: 3,
            maxWidth: 720,
            mx: "auto",
          }}
        >
          {TIERS.map((tier, i) => (
            <MotionBox
              key={tier.name}
              component="article"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                border: tier.highlighted
                  ? `2px solid ${theme.palette.primary.main}`
                  : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                boxShadow: tier.highlighted
                  ? `0 12px 32px ${alpha("#3b82f6", 0.15)}`
                  : "none",
                position: "relative",
              }}
            >
              {tier.highlighted && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -12,
                    left: "50%",
                    transform: "translateX(-50%)",
                    px: 2,
                    py: 0.5,
                    borderRadius: 99,
                    bgcolor: "primary.main",
                    color: "#fff",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                  }}
                >
                  RECOMMENDED
                </Box>
              )}
              <Typography
                component="h3"
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: tier.highlighted ? "primary.main" : "text.secondary",
                  mb: 1.5,
                }}
              >
                {tier.name}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.75,
                  mb: 1.5,
                }}
              >
                <Typography
                  component="data"
                  value={tier.priceValue}
                  sx={{
                    fontSize: tier.price.length > 6 ? "1.25rem" : "1.75rem",
                    fontWeight: 700,
                    color: "text.primary",
                  }}
                >
                  {tier.price}
                </Typography>
                {tier.unit && (
                  <Typography
                    sx={{ fontSize: "0.8rem", color: "text.secondary" }}
                  >
                    {tier.unit}
                  </Typography>
                )}
              </Box>
              <Typography
                component="p"
                sx={{
                  fontSize: "0.85rem",
                  color: "text.secondary",
                  mb: 2.5,
                  lineHeight: 1.5,
                }}
              >
                {tier.description}
              </Typography>

              {tier.meteredUnits && (
                <Box
                  component="ul"
                  sx={{
                    listStyle: "none",
                    p: 0,
                    m: 0,
                    mb: 2.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                  }}
                >
                  {tier.meteredUnits.map((mu) => (
                    <Box
                      key={mu.label}
                      component="li"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "0.8rem",
                        color: "text.secondary",
                        pb: 0.75,
                        borderBottom: `1px solid ${alpha(
                          theme.palette.divider,
                          0.15,
                        )}`,
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      >
                        {mu.label}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "0.8rem",
                          fontFamily: "monospace",
                          color: "text.secondary",
                        }}
                      >
                        {mu.price}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              <Typography
                component="a"
                href={tier.ctaHref}
                sx={{
                  display: "block",
                  textAlign: "center",
                  px: 2,
                  py: 1,
                  borderRadius: 1.5,
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  textDecoration: "none",
                  bgcolor: tier.highlighted
                    ? "primary.main"
                    : alpha("#3b82f6", 0.08),
                  color: tier.highlighted ? "#fff" : "primary.main",
                  border: tier.highlighted
                    ? "none"
                    : `1px solid ${alpha("#3b82f6", 0.3)}`,
                  "&:hover": {
                    bgcolor: tier.highlighted
                      ? "primary.dark"
                      : alpha("#3b82f6", 0.14),
                  },
                }}
              >
                {tier.cta}
              </Typography>
            </MotionBox>
          ))}
        </Box>
      </Container>

      {/* Product + Offer JSON-LD */}
      <JsonLd
        data={{
          "@type": "Product",
          name: "Rusha Platform",
          description:
            "Engineering control plane for building, deploying, and operating applications with unified GitOps, observability, FinOps, and AI recommendations.",
          brand: { "@type": "Brand", name: "Rusha" },
          offers: TIERS.map((t) => ({
            "@type": "Offer",
            name: `${t.name} plan`,
            price: t.priceValue,
            priceCurrency: "USD",
            description: t.description,
            url: t.ctaHref,
          })),
        }}
      />
    </Box>
  );
};

export default Pricing;
